import { useEffect } from 'react';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';

type Props = {
  dataPrefix: string | undefined;
  expandGroupForField: (fieldPath: string | undefined, dataPrefix: string | undefined) => void;
};

/**
 * Opens the collapsed group holding the annotation the user just navigated to.
 *
 * A collapsed group renders no `data-field` for its items, so an annotation inside one can be
 * neither seen nor focused. It expands for good rather than only while it holds the cursor:
 * re-collapsing as the user moves to the next annotation would pull rows out from under the one
 * they are moving to, and scroll them somewhere else.
 *
 * This renders nothing, and exists only to keep `useCurrentItem` out of the table itself. The table
 * subscribing would re-render every row and cell of every mounted table on each next/previous
 * click, which on a virtualized document is thousands of cells for a set that usually comes back
 * unchanged.
 */
export const FocusedGroupExpander = ({ dataPrefix, expandGroupForField }: Props) => {
  const { service } = useNavigationContext<NavigableItem>();
  const focusedFieldPath = service.useCurrentItem()?.fieldPath;

  useEffect(() => {
    expandGroupForField(focusedFieldPath, dataPrefix);
  }, [focusedFieldPath, dataPrefix, expandGroupForField]);

  return null;
};
